import { useState, type ReactNode } from 'react';

// SP-6c — client-side tri-state sorting helper shared by the customer/article
// tables inside the pick-list accordion. Click cycles asc → desc → none.

export type SortDir = 'asc' | 'desc' | null;

export function useSort(initialCol: string | null = null, initialDir: SortDir = null) {
  const [col, setCol] = useState<string | null>(initialCol);
  const [dir, setDir] = useState<SortDir>(initialDir);

  function toggle(c: string): void {
    if (col !== c) { setCol(c); setDir('asc'); return; }
    if (dir === 'asc') { setDir('desc'); return; }
    if (dir === 'desc') { setCol(null); setDir(null); return; }
    setDir('asc');
  }

  function reset(): void { setCol(initialCol); setDir(initialDir); }

  function sortRows<T>(rows: T[], get: (row: T, c: string) => unknown): T[] {
    if (col === null || dir === null) return rows;
    const c = col;
    const sign = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = get(a, c);
      const vb = get(b, c);
      let cmp: number;
      if (typeof va === 'string' || typeof vb === 'string') {
        cmp = String(va ?? '').localeCompare(String(vb ?? ''), 'it', { numeric: true });
      } else {
        cmp = Number(va ?? 0) - Number(vb ?? 0);
      }
      return sign * cmp;
    });
  }

  return { col, dir, toggle, reset, sortRows };
}

export interface SortThProps {
  label: ReactNode;
  sortKey: string;
  col: string | null;
  dir: SortDir;
  onToggle: (k: string) => void;
  className?: string;
  /** Optional tooltip on the header cell. */
  title?: string;
}

/** Clickable table header with a tri-state sort arrow (Bootstrap Icons). */
export function SortTh({ label, sortKey, col, dir, onToggle, className, title }: SortThProps) {
  const active = col === sortKey;
  const icon = !active
    ? 'bi-arrow-down-up text-muted'
    : dir === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  return (
    <th
      className={className}
      title={title}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onToggle(sortKey)}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label} <i className={`bi ${icon}`} />
    </th>
  );
}
